import { useState, useRef, useEffect } from "react";

export const InlineEditableText = ({
  value,
  onSave,
  isEditMode,
  placeholder = "Click to edit...",
  className = "",
  inputClassName = "",
  as = "span",
  multiline = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    setIsEditing(false);
    if (editValue !== value) {
      onSave(editValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !multiline) {
      handleSave();
    }
    if (e.key === "Escape") {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  const Tag = as;

  if (!isEditMode) {
    return <Tag className={className}>{value || placeholder}</Tag>;
  }

  if (isEditing) {
    if (multiline) {
      return (
        <textarea
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className={`w-full px-2 py-1 border border-teal-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none ${inputClassName}`}
          rows={3}
          placeholder={placeholder}
        />
      );
    }

    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className={`px-2 py-1 border border-teal-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 ${inputClassName}`}
        placeholder={placeholder}
      />
    );
  }

  return (
    <Tag
      className={`cursor-pointer hover:bg-teal-50 rounded px-1 -mx-1 transition-colors group ${className}`}
      onClick={() => setIsEditing(true)}
      title="Click to edit"
    >
      {value || <span className="text-gray-400 italic">{placeholder}</span>}
      <svg
        className="inline-block w-3.5 h-3.5 ml-1 text-teal-500 opacity-0 group-hover:opacity-100 transition-opacity"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
        />
      </svg>
    </Tag>
  );
};

export const InlineEditableListItem = ({
  value,
  onSave,
  onDelete,
  isEditMode,
  placeholder = "Enter text...",
  icon = "•",
  className = "",
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleSave = () => {
    setIsEditing(false);
    if (editValue.trim() !== value) {
      onSave(editValue.trim());
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSave();
    }
    if (e.key === "Escape") {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  if (!isEditMode) {
    return (
      <li className={`flex items-start gap-3 ${className}`}>
        <span className="text-current font-bold mt-0.5">{icon}</span>
        <span>{value}</span>
      </li>
    );
  }

  return (
    <li className={`flex items-start gap-2 group ${className}`}>
      <span className="text-current font-bold mt-2">{icon}</span>
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="flex-1 px-2 py-1 border border-teal-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
          placeholder={placeholder}
        />
      ) : (
        <span
          className="flex-1 cursor-pointer hover:bg-teal-50 rounded px-1 py-1 -mx-1 transition-colors"
          onClick={() => setIsEditing(true)}
          title="Click to edit"
        >
          {value || <span className="text-gray-400 italic">{placeholder}</span>}
          <svg
            className="inline-block w-3 h-3 ml-1 text-teal-500 opacity-0 group-hover:opacity-100 transition-opacity"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        </span>
      )}
      <button
        onClick={onDelete}
        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100"
        title="Delete"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
          />
        </svg>
      </button>
    </li>
  );
};

export const AddItemButton = ({ onClick, label = "Add item", className = "" }) => {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 text-sm text-teal-600 hover:bg-teal-50 rounded-lg transition-colors ${className}`}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
      {label}
    </button>
  );
};

export default InlineEditableText;
